import {useCallback, useState} from 'react';

import {
	makePatchRefundFinishRequest,
	makePostRefundUploadFile,
} from 'main/factories/usecases/private/refund/request';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {useRequestDataStore} from '../../Request/store/useRequestDataStore';
import {useSelectedRequestIndexStore} from '../../Request/store/useSelectedRequestIndexStore';
import {usePendingRefundUploadsStore} from '../store/usePendingRefundUploadsStore';

export function useFinishRefundV2() {
	const requestData = useRequestDataStore(state => state.data);
	const selectedRequestIndex = useSelectedRequestIndexStore(state => state.index);
	const pendingFiles = usePendingRefundUploadsStore(state => state.pendingFiles);
	const resetPendingFiles = usePendingRefundUploadsStore(state => state.resetPendingFiles);

	const [state, setState] = useState<OperationStateType>(OperationStateTypesEnum.NONE);

	const IS_LOADING = state === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR = state === OperationStateTypesEnum.ERROR;
	const IS_DONE = state === OperationStateTypesEnum.DONE;

	const resetState = useCallback(() => setState(OperationStateTypesEnum.NONE), []);

	const finish = useCallback(async () => {
		const protocolCode = requestData.protocolCode;
		const requestCode = requestData.requestData?.[selectedRequestIndex]?.requestCode ?? 0;

		try {
			setState(OperationStateTypesEnum.SUBMITTING);

			const filesToUpload = pendingFiles.filter(file => file.file && file.uploaded !== true);

			await Promise.all(
				filesToUpload.map(file =>
					makePostRefundUploadFile().post({
						file: file.file as File,
						'protocol-code': protocolCode,
						'request-code': requestCode,
					}),
				),
			);

			await makePatchRefundFinishRequest(protocolCode.toString()).patch();

			resetPendingFiles();
			setState(OperationStateTypesEnum.DONE);
		} catch (error) {
			setState(OperationStateTypesEnum.ERROR);
		}
	}, [pendingFiles, requestData, selectedRequestIndex, resetPendingFiles]);

	return {
		finish,
		resetState,
		IS_LOADING,
		IS_ERROR,
		IS_DONE,
	};
}
